import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Toaster, toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { startGoogleSignIn } from "@/lib/googleAuth";
import LabStamp from "@/components/LabStamp";
import { COMING_SOON } from "@/labConfig";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH. The redirect_url is always derived from
// window.location.origin so the user lands back on the same domain they came
// from.

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, loginEmail, registerEmail } = useAuth();
  const initialEmail = searchParams.get("email") || "";
  const [mode, setMode] = useState(initialEmail ? "signup" : "signin");
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Already signed in? Skip the form.
  useEffect(() => {
    if (user && user.user_id) {
      const target = location.state?.from || "/app/hebrew";
      navigate(target, { replace: true });
    }
  }, [user, navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === "signup" && !name.trim()) {
      toast.error("First name, please.");
      return;
    }
    setSubmitting(true);
    try {
      if (mode === "signup") {
        await registerEmail(email.trim().toLowerCase(), password, name.trim());
        toast.success("Account created. Welcome.");
      } else {
        await loginEmail(email.trim().toLowerCase(), password);
        toast.success("Welcome back.");
      }
      const target = location.state?.from || "/app/hebrew";
      navigate(target, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="relative min-h-screen w-full flex items-center justify-center px-6"
      style={{
        backgroundColor: "#FAF8F5",
        color: "#1A1523",
        fontFamily: "'Courier Prime', monospace",
      }}
      data-testid="login-page"
    >
      <Toaster position="bottom-right" richColors />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 80% 55% at 50% 0%, rgba(27,108,122,0.22) 0%, transparent 70%), radial-gradient(60% 45% at 20% 82%, rgba(46,140,158,0.35) 0%, transparent 72%)",
        }}
      />

      <div
        className="relative w-full max-w-[480px] rounded-2xl border p-6 sm:p-8 backdrop-blur-md"
        style={{
          borderColor: "rgba(26,21,35,0.10)",
          backgroundColor: "rgba(255,255,255,0.86)",
          boxShadow: "0 30px 80px -20px rgba(26,21,35,0.12)",
        }}
      >
        <div className="mb-6">
          <div className="mb-4">
            <LabStamp testId="login-lab-stamp" />
          </div>
          <Link
            to={COMING_SOON ? "/" : "/alephonics"}
            data-testid="login-home-link"
            className="text-xs uppercase tracking-[0.22em]"
            style={{ color: "rgba(26,21,35,0.6)" }}
          >
            &lsaquo; {COMING_SOON ? "Jewish Music Lab" : "Alephonics"}
          </Link>
          <h1
            className="mt-3 text-3xl sm:text-4xl leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            {mode === "signup" ? "Create your account." : "Welcome back."}
          </h1>
          <p className="mt-2 text-sm" style={{ color: "rgba(26,21,35,0.65)" }}>
            Step into The Alephonics App — save, share, and remix your scores.
          </p>
        </div>

        <button
          type="button"
          onClick={() => startGoogleSignIn(location.state?.from)}
          data-testid="login-google"
          className="w-full inline-flex items-center justify-center gap-3 rounded-full h-12 text-sm uppercase tracking-[0.18em]"
          style={{
            backgroundColor: "#FFFFFF",
            color: "#1A1523",
            border: "1px solid #D6CBB8",
            fontFamily: "'Courier Prime', monospace",
            fontWeight: 600,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.49h4.84a4.14 4.14 0 0 1-1.79 2.71v2.26h2.9c1.7-1.57 2.69-3.88 2.69-6.62z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.83.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.94v2.34A9 9 0 0 0 9 18z"/>
            <path fill="#FBBC04" d="M3.95 10.7A5.4 5.4 0 0 1 3.66 9c0-.59.1-1.16.29-1.7V4.96H.94A9 9 0 0 0 0 9c0 1.45.35 2.83.94 4.04l3.01-2.34z"/>
            <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .94 4.96L3.95 7.3C4.66 5.17 6.65 3.58 9 3.58z"/>
          </svg>
          Continue with Google
        </button>

        <div className="my-5 flex items-center gap-3">
          <div className="flex-1 h-px bg-[#F3EFE8]" />
          <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: "rgba(26,21,35,0.45)" }}>
            or
          </span>
          <div className="flex-1 h-px bg-[#F3EFE8]" />
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3" data-testid="login-form">
          {mode === "signup" && (
            <Input
              type="text"
              required
              placeholder="First name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="login-name"
              className="h-12 bg-white border-[#D6CBB8] text-[#1A1523] placeholder:text-[#8C8198]"
              style={{ fontFamily: "'Courier Prime', monospace" }}
            />
          )}
          <Input
            type="email"
            required
            placeholder="your address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email"
            autoComplete="email"
            className="h-12 bg-white border-[#D6CBB8] text-[#1A1523] placeholder:text-[#8C8198]"
            style={{ fontFamily: "'Courier Prime', monospace" }}
          />
          <Input
            type="password"
            required
            minLength={8}
            placeholder="Password (8+ characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password"
            autoComplete={mode === "signup" ? "new-password" : "current-password"}
            className="h-12 bg-white border-[#D6CBB8] text-[#1A1523] placeholder:text-[#8C8198]"
            style={{ fontFamily: "'Courier Prime', monospace" }}
          />
          <Button
            type="submit"
            data-testid="login-submit"
            disabled={submitting}
            className="h-12 rounded-full mt-1"
            style={{
              backgroundColor: "#1B6C7A",
              color: "#FFFFFF",
              border: "none",
              fontFamily: "'Courier Prime', monospace",
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            {submitting
              ? mode === "signup"
                ? "Creating…"
                : "Signing in…"
              : mode === "signup"
              ? "Create account"
              : "Sign in"}
          </Button>
        </form>

        <p className="mt-5 text-sm text-center" style={{ color: "rgba(26,21,35,0.7)" }}>
          {mode === "signup" ? (
            <>
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => setMode("signin")}
                data-testid="login-switch-signin"
                className="underline underline-offset-4 hover:text-[#8A6514]"
              >
                Sign in
              </button>
            </>
          ) : (
            <>
              New to Alephonics?{" "}
              <button
                type="button"
                onClick={() => setMode("signup")}
                data-testid="login-switch-signup"
                className="underline underline-offset-4 hover:text-[#8A6514]"
              >
                Create an account
              </button>
            </>
          )}
        </p>

        {/* The app is for Listening Club members — send everyone else to the
            club page rather than leaving them staring at a login. */}
        <div
          className="mt-6 pt-5 text-center"
          style={{ borderTop: "1px solid rgba(26,21,35,0.12)" }}
          data-testid="login-club-gate"
        >
          <p className="text-sm" style={{ color: "rgba(26,21,35,0.7)" }}>
            Not supporting the music yet?
          </p>
          <Link
            to="/patrons"
            data-testid="login-patrons-link"
            className="mt-3 inline-flex items-center justify-center whitespace-nowrap rounded-full px-6 h-11 text-xs uppercase transition-colors hover:bg-[#D99B26]/[0.14]"
            style={{
              border: "1px solid #D99B26",
              color: "#8A6514",
              letterSpacing: "0.18em",
              fontWeight: 500,
              fontFamily: "'Courier Prime', monospace",
            }}
          >
            Click here to get more info
          </Link>
        </div>
      </div>
    </div>
  );
}
